import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import { Fancybox } from "@fancyapps/ui/src/Fancybox";
import ApplicationController from "./application_controller";
import { loadGoogleCharts, drawChartFromData } from "../utils/charts";

export default class extends ApplicationController {
  static targets = [
    "grid",
    "chip",
    "current_app",
    "widgetCategory",
    "searchInput",
    "noResultMessage",
  ];

  static values = {
    url: String,
    dashId: Number,
    currentApp: String,
    popupMessageUrl: String,
  };

  connect() {
    this.changeColor();
    this.selectedChips = new Set();

    if (this.hasPopupMessageUrlValue) {
      this.getPopupMessage();
    }

    this.chipTargets.forEach((chip) => {
      if (chip.classList.contains("selected")) {
        const widgetId = chip.dataset.id;
        this.selectedChips.add(widgetId);
      }
    });

    this.selectedChips = new Set();
    this.chipTargets.forEach((chip) => {
      if (chip.classList.contains("selected")) {
        this.selectedChips.add(chip.dataset.id);
      }
    });

    if (this.hasGridTarget) this.initGridStack();
    if (this.hasGridTarget && this.grid) this.compactAllWidgetsUp();
    if (!this.hasUrlValue || !this.hasDashIdValue) {
      console.warn("⚠️ dashloader: url or dashId is missing, skipping AJAX.");
    } else {
      const dashId = this.dashIdValue;
      const url = this.urlValue;

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          this.element.innerHTML = html;
          // ⬇️ Recharge Stimulus si besoin
          this.reconnectStimulusControllers(this.element);

          // ⬇️ Réapplique GridStack
          this.initGridStack();

          const eventName = `dash:load_${this.dashIdValue}`;
          document.dispatchEvent(new CustomEvent(eventName));
        })
        .catch((error) => {
          this.element.innerHTML =
            "<div class='alert alert-danger'>Erreur lors du chargement du widget.</div>";
          console.error("❌ Erreur AJAX dashloader :", error);
        });
    }

    // Écouter l'événement dispatché depuis `fancybox_controller`
    document.addEventListener(
      "dashboard:reflex-done",
      this.handleReflexDone.bind(this)
    );
  }

  initGridStack() {
    if (!this.hasGridTarget) {
      console.info("Il n'y a pas de gridStack dans le DOM");
      return;
    }

    this.grid = GridStack.init(
      {
        handle: ".drag-handle",
        cellHeight: 5,
        animate: true,
        float: true,
        resizable: { handles: "all" },
      },
      this.gridTarget
    );

    window.myDashboard = {
      grid: this.grid,
      currentPage: parseInt(
        new URLSearchParams(window.location.search).get("page") || 1
      ),
      dashId: this.dashIdValue,
    };
    this.grid.on("change", (event, items) => {
        const iir = event.currentTarget.closest(".grid-stack-item");
    //  console.log("🈴🈴🈴🈴🈴🈴 Dans le change", iir);
    // console.log(" 🈴🈴🈴🈴🈴🈴",items.length)
    // console.log(" 🈴🈴🈴🈴🈴🈴",event)

      event.preventDefault();
      event.stopImmediatePropagation();

      const payload = items.map((item) => {
        const widgetId = item.el.dataset.id;
        const config = {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          auto_position: item.autoPosition || false,
          no_resize: item.noResize || false,
          no_move: item.noMove || false,
        };
        return { id: widgetId, grid_config: config };
      });

      fetch(`/${this.currentAppValue}/fdashboards/update_all_settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
        body: JSON.stringify({ settings: payload }),
      });

      // 👇 tout le reste de ton code (recalc hauteur, redraw charts...)
      items.forEach((item) => {
        const colCount = this.grid.opts.column;
        const gridWidth = this.grid.el.clientWidth;

        const cellWidth = gridWidth / colCount;

        const wasResizedByUser = item.el.dataset.userResized === "true";
        const wasMovedByUser = item.el.dataset.userMoved === "true";

        delete item.el.dataset.userResized;
        delete item.el.dataset.userMoved;

        const graphContainer = item.el.querySelector("[data-chart-data]");
        const contentWrapper = item.el.querySelector(
          ".content.table-scroll-wrapper"
        );
        const contentWrapperContentSize = this.getContentSize(contentWrapper);

        let gridStackItemContentHeight = this.getFullHeight(
          item.el.querySelector(".grid-stack-item-content")
        );

        let moreDetailsBlockHeight = 0;
        const moreDetailsBlock = item.el.querySelector(
          "[data-chart-more-details]"
        );
        if (moreDetailsBlock) {
          moreDetailsBlockHeight = 44;
        }

        let chartToolsHeight = 0;
        const chartToolsBlock = item.el.querySelector(
          "[data-export-csv-label]"
        );
        if (chartToolsBlock) {
          chartToolsHeight = 26;
        }

        let extendTitleHeight = 0;
        const extendTitle = item.el.querySelector(".extend_title");
        if (extendTitle) {
          extendTitleHeight = this.getFullHeight(extendTitle);
        }

        let barreTitreHeight = 0;
        const barreTitre = item.el.querySelector(".barre-titre");
        if (barreTitre) {
          barreTitreHeight = this.getFullHeight(barreTitre);
        }

        const contentWrapperGap =
          chartToolsHeight +
          moreDetailsBlockHeight +
          barreTitreHeight +
          extendTitleHeight;

        let finalHeight =
          gridStackItemContentHeight - contentWrapperGap - 26 - 36;

        if (graphContainer?.dataset.chartData) {
          this.isChartWidget = true;
          loadGoogleCharts(() =>
            drawChartFromData(graphContainer, {
              chartWidth: item.w * cellWidth - 60,
              chartHeight: finalHeight,
              dashId: this.dashIdValue,
            })
          );
          //contentWrapper.style.height = `${contentWrapper.offsetHeight}px`;
          contentWrapper.style.height = `${
            gridStackItemContentHeight -
            barreTitreHeight -
            extendTitleHeight -
            36
          }px`;
        } else {
          if (
            contentWrapperContentSize?.height <
            gridStackItemContentHeight - barreTitreHeight - extendTitleHeight
          ) {
            contentWrapper.style.height = contentWrapperContentSize?.height + 5;
          } else {
            if (
              contentWrapper &&
              contentWrapper.style &&
              contentWrapper.style.height
            ) {
              contentWrapper.style.height = `${contentWrapper.style.height}px`;
            } else {
            }
          }
        }
      });
    });

    this.grid.on("dragstart", (_, el) => {
      // Ajouter un style visuel uniquement sur l'élément en train d'être déplacé
      el.classList.add("dragging-simulated");

      // Appliquer une mise en surbrillance sur les autres
      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("dragstop", (_, el) => {
      // Nettoyer tous les styles
      el.classList.remove("dragging-simulated");
      el.dataset.userMoved = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.grid.on("resizestart", (_, el) => {
      el.classList.add("resizing-simulated");

      el.style.removeProperty("height");

      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("resizestop", (_, el) => {
      el.classList.remove("resizing-simulated");
      el.style.removeProperty("height");
      el.dataset.userResized = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.toggleGridStackItems();
  }

  searchWidgets(event) {
    const normalize = (str) =>
      str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const query = normalize(event.target.value);
    const chips = this.element.querySelectorAll(".chip");
    const categories = this.element.querySelectorAll(".widget-category");
    let totalVisibleChips = 0;

    chips.forEach((chip) => {
      const text = normalize(chip.textContent);
      const isVisible = text.includes(query);
      chip.style.display = isVisible ? "" : "none";
      if (isVisible) totalVisibleChips++;
    });

    categories.forEach((category) => {
      const visibleChips = category.querySelectorAll(
        '.chip:not([style*="display: none"])'
      );
      category.style.display = visibleChips.length > 0 ? "" : "none";
    });

    if (this.hasNoResultMessageTarget) {
      this.noResultMessageTarget.style.display =
        totalVisibleChips === 0 ? "flex" : "none";
    }
  }

  toggleGridStackItems() {
    document
      .querySelectorAll(".grid-stack-item")
      .forEach((e) => e.classList.remove("fx-d-none"));
    const skeleton = document.getElementById("fx-widgets-skeleton");
    skeleton?.classList.add("fx-d-none");
    skeleton?.classList.remove("fx-d-flex");
  }

  changePage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const page = event.currentTarget.dataset.page;
    this.stimulate("FdashboardReflex#change_page", page);
  }

  handleReflexDone(event) {
    const { id, page, widgetid, reflex } = event.detail;

    if (reflex === "dash_setting#destroy") {
      const el = document.querySelector(`.grid-stack-item[data-id='${id}']`);
      if (el && this.grid) {
        var fx_wid_count_page = document.getElementById(
          `fx_wid_count_page_${page}`
        );
        var fx_wid_chip = document.getElementById(`fx_wid_chip_${widgetid}`);
        var fx_wid_chip_page_number = document.getElementById(
          `fx_wid_chip_page_number_${widgetid}`
        );
        var fx_wid_chip_add_to_page = document.getElementById(
          `fx_wid_chip_add_to_page_${widgetid}`
        );
        var fx_wid_chip_move_to_page = document.getElementById(
          `fx_wid_chip_move_to_page_${widgetid}`
        );

        if (fx_wid_count_page) {
          let count = parseInt(fx_wid_count_page.textContent, 10);
          if (!isNaN(count) && count > 1) {
            fx_wid_count_page.textContent = count - 1;
          } else {
            fx_wid_count_page.classList.add("fx-d-none");
          }
        }

        if (fx_wid_chip) {
          fx_wid_chip.classList.remove("selected");
        }

        if (fx_wid_chip_add_to_page) {
          fx_wid_chip_add_to_page.classList.remove("fx-d-none");
        }

        if (fx_wid_chip_page_number) {
          fx_wid_chip_page_number.classList.add("fx-d-none");
        }

        if (fx_wid_chip_move_to_page) {
          fx_wid_chip_move_to_page.classList.add("fx-d-none");
        }

        this.grid.removeWidget(el);
        //this.grid.compact(); ne plus compacter aprés supression de widget.
      }
    }
  }

  moveToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const widgetId = event.currentTarget.dataset.id;
    const targetPage = event.currentTarget.dataset.targetPage;
    const settingId = event.currentTarget.dataset.settingId;
    const current_app = event.currentTarget.dataset.currentApp;
    const currentPage = event.currentTarget.dataset.currentPage;
    const sourcePage = event.currentTarget.dataset.sourcePage;
    const graphType = event.currentTarget.dataset.graphType;

    const fx_wid_move_to_page_btn = document.getElementById(
      `fx_wid_move_to_page_btn_${targetPage}`
    );
    const fx_wid_move_to_page_btn_loader = document.getElementById(
      `fx_wid_move_to_page_btn_loader_${targetPage}`
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${widgetId}`
    );

    // je recipere le chip dès le départ, avant que le DOM ne bouge
    const chip = event.currentTarget.closest(".chip");

    if (fx_wid_move_to_page_btn)
      fx_wid_move_to_page_btn.classList.add("fx-d-none");
    if (fx_wid_move_to_page_btn_loader)
      fx_wid_move_to_page_btn_loader.classList.remove("fx-d-none");

    fetch(`/${current_app}/fdashboards/move_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({
        dash_library_id: widgetId,
        new_page: targetPage,
        setting_id: settingId,
      }),
    })
      .then((res) => {
        if (fx_wid_move_to_page_btn)
          fx_wid_move_to_page_btn.classList.remove("fx-d-none");
        if (fx_wid_move_to_page_btn_loader)
          fx_wid_move_to_page_btn_loader.classList.add("fx-d-none");

        if (!res.ok) {
          console.error("❌ move_widget: requête échouée");
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (!data || !chip) return;

        if (fx_wid_chip_page_number) {
          fx_wid_chip_page_number.textContent = "page " + targetPage;
        }

        const el = document.querySelector(
          `.grid-stack-item[data-id='${settingId}']`
        );
        if (el && window.myDashboard?.grid) {
          window.myDashboard.grid.removeWidget(el);
        }

        const sourceCount = document.getElementById(
          `fx_wid_count_page_${sourcePage}`
        );
        const targetCount = document.getElementById(
          `fx_wid_count_page_${targetPage}`
        );

        if (sourceCount) {
          let count = parseInt(sourceCount.textContent, 10);
          if (!isNaN(count) && count > 1) {
            sourceCount.textContent = count - 1;
          } else {
            sourceCount.classList.add("fx-d-none");
          }
        }

        if (targetCount) {
          let count = parseInt(targetCount.textContent, 10);
          if (!isNaN(count)) {
            targetCount.textContent = count + 1;
            targetCount.classList.remove("fx-d-none");
          }
        }

        // Réorganise dropdown localement dans le chip déjà capturé
        const sourceDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${sourcePage}`)
          ?.closest(".dropdown-item");
        const targetDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${targetPage}`)
          ?.closest(".dropdown-item");

        if (sourceDropdownItem)
          sourceDropdownItem.classList.remove("fx-d-none");
        if (targetDropdownItem) targetDropdownItem.classList.add("fx-d-none");

        // Met à jour tous les boutons de ce dropdown
        const dropdown = chip.querySelector(".fx-dropdown-content");
        if (dropdown) {
          dropdown
            .querySelectorAll(`[id^='fx_wid_move_to_page_btn_']`)
            .forEach((link) => {
              link.dataset.sourcePage = targetPage;
            });
        }

        if (data.status === "ok" && currentPage === targetPage) {
          this.addToTargetPage(
            widgetId,
            current_app,
            data.setting_id,
            data.grid_config,
            graphType
          );
        }
      });
  }

  addToTargetPage(
    dashLibraryId,
    current_app,
    setting_id,
    grid_config,
    graphType
  ) {
    fetch(
      `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}`
    )
      .then((res) => res.text())
      .then((html) => {
        const el = document.createElement("div");
        el.classList.add("grid-stack-item");
        el.dataset.id = setting_id;

        el.setAttribute("gs-x", grid_config.x);
        el.setAttribute("gs-y", grid_config.y);
        el.setAttribute("gs-w", grid_config.w);
        el.setAttribute("gs-h", grid_config.h);
        el.setAttribute("gs-auto-position", grid_config.auto_position);
        el.setAttribute("gs-no-resize", grid_config.no_resize);
        el.setAttribute("gs-no-move", grid_config.no_move);

        el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
            ${html}
          </div>
        `;

        if (window.myDashboard?.grid) {
          window.myDashboard.grid.el.appendChild(el);
          window.myDashboard.grid.makeWidget(el);
        }

        $("#cross_widget table").addClass(
          "fx-table-hm cross fx-table-hm-wrapped fx-table-12"
        );
        $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
      });
  }
  guardDisabled(event) {
      const container = event.currentTarget.closest('[aria-disabled="true"]');
      if (container && container.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
    }
  addToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const dashLibraryId = event.currentTarget.dataset.id;
    const dash_page = event.currentTarget.dataset.dashPage;

    const num_page = event.currentTarget.dataset.targetPage;
    const current_page = event.currentTarget.dataset.currentPage;
    const current_app = event.currentTarget.dataset.currentApp;
    const graphType = event.currentTarget.dataset.graphType;
    const fx_wid_count_target_page = document.getElementById(
      `fx_wid_count_page_${num_page}`
    );
    const fx_wid_count_current_page = document.getElementById(
      `fx_wid_count_page_${current_page}`
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${dashLibraryId}`
    );
    const fx_wid_chip_add_to_page = document.getElementById(
      `fx_wid_chip_add_to_page_${dashLibraryId}`
    );
    const fx_wid_chip_move_to_page = document.getElementById(
      `fx_wid_chip_move_to_page_${dashLibraryId}`
    );
    const fx_wid_chip = document.getElementById(`fx_wid_chip_${dashLibraryId}`);

    const fx_wid_add_to_page_btn = document.getElementById(
      `fx_wid_add_to_page_btn_${num_page}`
    );
    const fx_wid_add_to_page_btn_loader = document.getElementById(
      `fx_wid_add_to_page_btn_loader_${num_page}`
    );

    if (fx_wid_add_to_page_btn) {
      fx_wid_add_to_page_btn.classList.add("fx-d-none");
    }

    if (fx_wid_add_to_page_btn_loader) {
      fx_wid_add_to_page_btn_loader.classList.remove("fx-d-none");
    }

    fetch(`/${current_app}/fdashboards/add_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
      },
      body: JSON.stringify({
        dash_library_id: dashLibraryId,
        num_page: num_page,
        dash_page: dash_page,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const { setting_id, grid_config } = data;
        return fetch(
          `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}`
        )
          .then((res) => res.text())
          .then((html) => {
            const el = document.createElement("div");
            el.classList.add("grid-stack-item");
            el.dataset.id = setting_id;

            el.setAttribute("gs-x", grid_config.x);
            el.setAttribute("gs-y", grid_config.y);
            el.setAttribute("gs-w", grid_config.w);
            el.setAttribute("gs-h", grid_config.h);
            el.setAttribute("gs-auto-position", grid_config.auto_position);
            el.setAttribute("gs-no-resize", grid_config.no_resize);
            el.setAttribute("gs-no-move", grid_config.no_move);

            el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
           
                        ${html} 
          </div>
        `;

            if (fx_wid_add_to_page_btn) {
              fx_wid_add_to_page_btn.classList.remove("fx-d-none");
            }

            if (fx_wid_add_to_page_btn_loader) {
              fx_wid_add_to_page_btn_loader.classList.add("fx-d-none");
            }

            if (num_page == current_page) {
              if (window.myDashboard?.grid)
                window.myDashboard.grid.makeWidget(el);
            } else {
            }

            if (fx_wid_count_target_page) {
              let count = parseInt(fx_wid_count_target_page.textContent, 10);
              if (!isNaN(count)) {
                fx_wid_count_target_page.textContent = count + 1;
                fx_wid_count_target_page.classList.remove("fx-d-none");
              }
            }

            if (fx_wid_chip) {
              fx_wid_chip.classList.add("selected");
            }

            if (fx_wid_chip_add_to_page) {
              fx_wid_chip_add_to_page.classList.add("fx-d-none");
            }

            if (fx_wid_chip_page_number) {
              fx_wid_chip_page_number.classList.remove("fx-d-none");
              fx_wid_chip_page_number.textContent = "page " + num_page;
            }

            if (fx_wid_chip_move_to_page) {
              fx_wid_chip_move_to_page.classList.remove("fx-d-none");

              // Mise à jour de tous les <a> dans le dropdown
              const dropdownLinks = fx_wid_chip_move_to_page.querySelectorAll(
                "a[data-target-page]"
              );
              dropdownLinks.forEach((link) => {
                link.dataset.sourcePage = num_page;
                link.dataset.settingId = setting_id;
              });
              // Cacher le bouton move_to_page "Déplacer vers la page (page_actuelle)" dans le dropdown
              const sourceDropdownItem = fx_wid_chip_move_to_page
                .querySelector(`#fx_wid_move_to_page_btn_${num_page}`)
                ?.closest(".dropdown-item");
              if (sourceDropdownItem)
                sourceDropdownItem.classList.add("fx-d-none");
            }

            $("#cross_widget table").addClass(
              "fx-table-hm cross fx-table-hm-wrapped fx-table-12"
            );
            $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
          });
      });
  }

  compact() {
    if (this.grid) {
      // this.grid.compact();
      this.compactYOnly(this.grid);
    }
  }

  openWidgetSelection(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (typeof Fancybox !== "undefined") {
      Fancybox.show([
        {
          src: "#widget-selection",
          type: "inline",
        },
      ]);
    } else {
      console.warn("⚠️ Fancybox non chargé !");
    }
  }
  setFixedHeight(itemEl, fixedPx = 250) {
    if (!itemEl || !window.myDashboard?.grid) {
      console.warn("⛔️ Pas d’élément grid-stack ou GridStack non dispo.");
      return;
    }

    // Même early-return que resizeToContentFull pour les charts
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper"
    );

    // On garde la même logique d'accès aux tailles (mais on n'en dépend pas pour la hauteur)
    const contentWrapperContentSize = this.getContentSize(contentWrapper);
    const extendTitleHeight = this.getFullHeight(
      itemEl.querySelector(".extend_title")
    );
    const barreTitreHeight = this.getFullHeight(
      itemEl.querySelector(".barre-titre")
    );

    if (!container || !widgetId) {
      console.warn("⛔️ Container ou widgetId manquant.");
      return;
    }

    // Identique à resizeToContentFull pour récupérer la grille et les tailles de cellule
    const cellHeight = window.myDashboard.grid.getCellHeight(true);
    const colCount = window.myDashboard.grid.opts.column;
    const gridWidth = window.myDashboard.grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    // Hauteur forcée (remplace le calcul "contenu + titres + marges")

    const containerHeight =
      barreTitreHeight +
        extendTitleHeight +
        contentWrapperContentSize?.height +
        36 || container.offsetHeight;
    const contentWidth =
      contentWrapperContentSize?.width || container.offsetWidth;
    const forcedContainerHeight =
      containerHeight <= fixedPx ? containerHeight : fixedPx;
    const newHeight = Math.ceil(forcedContainerHeight / cellHeight);

    // On peut laisser la largeur telle quelle (conservée) OU recalculer comme dans resizeToContentFull.
    // Ici on suit ta logique d'origine pour rester "exactement comme" (y compris adaptation horizontale).
    let newWidth = Math.min(colCount, Math.ceil(contentWidth / cellWidth));
    if (contentWidth > newWidth * cellWidth && newWidth < colCount) {
      newWidth += 1;
    }

    const node = window.myDashboard.grid.engine.nodes.find(
      (n) => n.el === itemEl
    );
    if (!node) {
      console.warn("⛔️ Node GridStack introuvable.");
      return;
    }

    // Appliquer les dimensions (comme dans resizeToContentFull)
    window.myDashboard.grid.update(itemEl, {
      width: newWidth,
      height: newHeight,
      x: node.x,
      y: node.y,
    });

    // Forcer le redraw immédiat (mêmes “nudges” y+1/y-1)
    node.w = newWidth;
    node.h = newHeight;

    window.myDashboard.grid.update(itemEl, { y: node.y + 1 });
    window.myDashboard.grid.update(itemEl, { y: node.y - 1 });

    window.myDashboard.grid._triggerChangeEvent();
    window.myDashboard.grid.engine._packNodes(); // (identique à resizeToContentFull)
    window.myDashboard.grid.engine.save();

    // Sauvegarde côté serveur (copié de resizeToContentFull)
    const config = {
      x: node.x,
      y: node.y,
      w: newWidth,
      h: newHeight,
      auto_position: node.autoPosition || false,
      no_resize: node.noResize || false,
      no_move: node.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Serveur a retourné une erreur");
      })
      .catch((err) => {
        console.error("❌ Erreur fetch:", err);
      });

    // Animation visuelle (idem)
    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");
    this.compactYOnlyWidgetsBelow(window.myDashboard.grid, node);
  }

  resizeToContentFull(event) {
   
    const itemEl = event.currentTarget.closest(".grid-stack-item");
    //  console.log("Dans le resizeToContentFull", itemEl);
    const grid = window.myDashboard?.grid;
    if (!itemEl || !grid) {
      console.warn("⛔️ Pas d’élément grid-stack ou GridStack non dispo.");
      return;
    }

    // Cas charts : anim + exit
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper"
    );
    if (!container || !widgetId) {
      console.warn("⛔️ Container ou widgetId manquant.");
      return;
    }

    // Mesures utilitaires
    const getFullHeight = (el) => {
      if (!el) return 0;
      const cs = window.getComputedStyle(el);
      return (
        el.offsetHeight +
        parseFloat(cs.marginTop || 0) +
        parseFloat(cs.marginBottom || 0)
      );
    };
    const getContentSize = (el) => {
      if (!el)
        return { width: container.offsetWidth, height: container.offsetHeight };
      return { width: el.scrollWidth, height: el.scrollHeight };
    };

    const contentSize = getContentSize(contentWrapper);
    const extendTitleHeight = getFullHeight(
      itemEl.querySelector(".extend_title")
    );
    const barreTitreHeight = getFullHeight(
      itemEl.querySelector(".barre-titre")
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const containerHeight =
      barreTitreHeight + extendTitleHeight + (contentSize?.height || 0) + 36 ||
      container.offsetHeight;
    const contentWidth = contentSize?.width || container.offsetWidth;

    const newHeight = Math.max(1, Math.ceil(containerHeight / cellHeight));
    let newWidth = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth))
    );
    if (contentWidth > newWidth * cellWidth && newWidth < colCount)
      newWidth += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) {
      console.warn("⛔️ Node GridStack introuvable.");
      return;
    }

    // ===== 1) Snapshot "avant" pour garder l'ordre initial (rank) =====
    const before = grid.engine.nodes.map((n) => ({
      id: n.el?.dataset.id,
      x: n.x,
      y: n.y,
      w: n.w,
      h: n.h,
      el: n.el,
    }));
    const orderRank = new Map(
      [...before]
        .sort((a, b) => a.y - b.y || a.x - b.x)
        .map((n, i) => [n.id, i])
    );
    const resizedId = itemEl.dataset.id;
    const prevY = before.find((n) => n.id === resizedId)?.y ?? node.y;

    // ===== 2) Resize + "y nudge" (on garde exactement ta logique) =====
    grid.update(itemEl, {
      width: newWidth,
      height: newHeight,
      x: node.x,
      y: node.y + 1,
    });
    // mettre à jour l'état local comme tu le fais
    node.w = newWidth;
    node.h = newHeight;
    grid.update(itemEl, { y: node.y - 1 });

    // ===== 3) Stabilisation d’ordre des widgets poussés =====
    // On remet les widgets déplacés dans le même ordre relatif qu’avant (par colonne)
    const after = grid.engine.nodes.map((n) => ({
      id: n.el?.dataset.id,
      x: n.x,
      y: n.y,
      w: n.w,
      h: n.h,
      el: n.el,
    }));

    // candidats "poussés" : y a changé et se trouvent dans/au-dessous de la zone impactée
    const pushed = after
      .filter((n) => n.id !== resizedId)
      .map((n) => ({ after: n, before: before.find((b) => b.id === n.id) }))
      .filter(
        (p) =>
          p.before &&
          p.before.y !== p.after.y &&
          p.after.y >= Math.min(prevY, node.y)
      );

    if (pushed.length) {
      // groupe par colonne
      const byCol = new Map();
      for (const p of pushed) {
        const col = p.after.x;
        if (!byCol.has(col)) byCol.set(col, []);
        byCol.get(col).push(p);
      }

      grid.batchUpdate();
      try {
        for (const [col, arr] of byCol) {
          // tri par ordre initial (rank avant)
          arr.sort(
            (a, b) => orderRank.get(a.after.id) - orderRank.get(b.after.id)
          );

          // point de départ: plus petit y actuel des poussés dans cette colonne
          let yCursor = Math.min(...arr.map((p) => p.after.y));
          for (const p of arr) {
            const h = p.after.h;
            if (p.after.y !== yCursor) {
              grid.update(p.after.el, { y: yCursor });
            }
            yCursor += h; // empilement vertical, garde l'ordre relatif
          }
        }
      } finally {
        grid.commit();
      }
    }

    // ===== 4) Persistance: un HASH {x,y,w,h} (ta vue reste inchangée) =====
    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Serveur a retourné une erreur");
      })
      .catch((err) => {
        console.error("❌ Erreur fetch:", err);
      });

    // ===== 5) Anim & cleanup =====
    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");

    // ⚠️ Si cette méthode re-compacte en changeant encore l’ordre, commente-la.
    this.compactYOnlyWidgetsBelow(window.myDashboard.grid, node);
  }

  // Fonction pour récupérer la hauteur d'un élément
  getElementHeight(parent, selector, defaultHeight = 0) {
    const element = parent.querySelector(selector);
    if (element) {
      return defaultHeight;
    }
    return 0;
  }

  compactYOnly(grid) {
    if (!grid?.engine?.nodes) return;

    // 🧩 Tri par colonne, puis par Y croissant
    const sorted = grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;
      // On remonte tant qu’il y a une collision
      while (
        grid.engine.collide(node, { x: node.x, y: newY, w: node.w, h: node.h })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved y=${node.y} → y=${newY}`
        // );
        grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    grid._triggerChangeEvent();
    grid._updateStyles(true);
  }

  compactYOnlyWidgetsBelow(grid, refNode) {
    if (!grid?.engine?.nodes || !refNode) {
      console.warn("⛔️ Grid or refNode not available.");
      return;
    }

    const nodes = grid.engine.nodes;

    const refX1 = refNode.x;
    const refX2 = refNode.x + refNode.w - 1;
    let refBottom = refNode.y + refNode.h;

    // 🎯 Widgets situés sous le refNode et qui se chevauchent horizontalement
    const targets = nodes
      .filter(
        (n) =>
          n !== refNode &&
          n.y >= refBottom &&
          n.x <= refX2 &&
          n.x + n.w - 1 >= refX1
      )
      .sort((a, b) => a.y - b.y || a.x - b.x);

    targets.forEach((node) => {
      let newY = refBottom;

      while (
        grid.engine.collide(node, { x: node.x, y: newY, w: node.w, h: node.h })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Moving widget ${node.el?.dataset?.id} from y=${node.y} to y=${newY}`
        // );
        grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }

      // 🔄 Mise à jour du bas de la zone pour les suivants
      refBottom = Math.max(refBottom, newY + node.h);
    });

    grid._triggerChangeEvent();
    grid._updateStyles(true);
  }

  compactAllWidgetsUp() {
    if (!this.grid?.engine?.nodes) {
      console.warn("⛔️ Grid not available.");
      return;
    }

    // 🧩 Tri stable : par colonne, puis par Y croissant
    const sorted = this.grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;

      // On cherche la première position libre vers le haut
      while (
        this.grid.engine.collide(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved from y=${node.y} → y=${newY}`
        // );
        this.grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    // console.log("🔄 Triggering change + updating styles (global compact up)");
    this.grid._triggerChangeEvent();
    this.grid._updateStyles(true);
  }

  reconnectStimulusControllers(container) {
    container.querySelectorAll("[data-controller]").forEach((el) => {
      const controllers = el.getAttribute("data-controller").split(" ");
      controllers.forEach((controller) => {
        application
          .getControllerForElementAndIdentifier(el, controller)
          ?.connect();
      });
    });
  }

  getPopupMessage() {
    fetch(this.popupMessageUrlValue, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const popup = document.getElementById("popup-content");

        if (popup) {
          popup.innerHTML = html;

          // Vérifie si #fx_modal existe dans le contenu injecté
          const modal = popup.querySelector("#fx_modal");

          if (modal) {
            Fancybox.show([{ src: "#popup-content", type: "inline" }]);
          }
        }
      })
      .catch((error) => {
        console.error("Erreur lors du chargement du popup :", error);
      });
  }
  toggleAutoresize(event) {
    const itemEl = event.currentTarget.closest(".grid-stack-item");
    const dashSettingId = itemEl.dataset.id;
    const icon = event.currentTarget.querySelector("i");

    const currentValue = parseInt(icon.dataset.autoResize, 10);
    const isEnabled = currentValue === 1;

    // Toggle dataset
    icon.dataset.autoResize = isEnabled ? 2 : 1;

    // Toggle classes
    icon.classList.toggle("fa-toggle-on", !isEnabled);
    icon.classList.toggle("fa-toggle-off", isEnabled);

    // Toggle title
    icon.title = isEnabled
      ? "Redimensionnement auto désactivé"
      : "Redimensionnement auto activé";

    const newValue = isEnabled ? 2 : 1;

    fetch(
      `/${this.currentAppValue}/fdashboards/${dashSettingId}/toggle_auto_resize`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
        body: JSON.stringify({ auto_resize: newValue }),
      }
    );
    if (newValue === 1) {
      // auto-resize activé → comportement normal
      this.resizeToContentFull(event);
    } else {
      // auto-resize désactivé → hauteur forcée 250px
      this.setFixedHeight(itemEl);
    }
  }
}
